<template>
  <div class="ocard" role="dialog" aria-label="Карточка объекта">
    <div class="ocard__head">
      <div>
        <div class="ocard__name">{{ location.name || '—' }}</div>
        <div class="ocard__addr">{{ location.address || 'Адрес не указан' }}</div>
      </div>
      <button type="button" class="btn btn-sm btn-light ocard__close" aria-label="Закрыть" @click="$emit('close')">×</button>
    </div>

    <div class="ocard__chips">
      <span class="ocard__chip" :class="'ocard__chip--' + conn.key">{{ conn.label }}</span>
      <span v-if="sleeping" class="ocard__chip ocard__chip--sleeping">Спящий режим</span>
    </div>

    <div v-if="toner" class="ocard__cmyk">
      <div class="ocard__label">Уровни CMYK</div>
      <div class="ocard__cmyk-grid">
        <div v-for="t in toner" :key="t.key" class="ocard__cmyk-row">
          <div class="ocard__bar"><div class="ocard__bar-fill" :style="{ width: (t.percent === null ? 0 : t.percent) + '%', background: cmykColors[t.key] }"></div></div>
          <div class="ocard__cmyk-txt"><span>{{ t.key }}</span><span>{{ t.percent === null ? '—' : t.percent + '%' }}</span></div>
        </div>
      </div>
    </div>

    <div class="ocard__paper">
      <span class="ocard__paper-label"><i class="ri-stack-line" aria-hidden="true"></i> Остаток бумаги на объекте</span>
      <span class="ocard__paper-value"><b>{{ paper.num }}</b><span v-if="paper.unit" class="ocard__muted"> {{ paper.unit }}</span></span>
    </div>

    <div class="ocard__problems">
      <div class="ocard__label">Проблемы</div>
      <div v-if="!problems" class="ocard__muted">Нет данных о принтере</div>
      <template v-else-if="problems.errors.length || problems.warnings.length">
        <div v-if="problems.errors.length" class="ocard__plist ocard__plist--error">
          <div class="ocard__ptitle">Критические проблемы</div>
          <ul><li v-for="(d, i) in problems.errors" :key="'e' + i">{{ d }}</li></ul>
        </div>
        <div v-if="problems.warnings.length" class="ocard__plist ocard__plist--service">
          <div class="ocard__ptitle">Требуется обслуживание</div>
          <ul><li v-for="(d, i) in problems.warnings" :key="'w' + i">{{ d }}</li></ul>
        </div>
      </template>
      <div v-else class="ocard__ok">Проблем нет</div>
    </div>

    <div v-if="interfaceUrl" class="ocard__links">
      <a class="ocard__iface" :href="interfaceUrl" target="_blank" rel="noopener noreferrer">Интерфейс принтера</a>
    </div>

    <CardTaskBlock
      :target="location"
      :active-task="activeTask"
      :extra-active-count="extraActiveCount"
      :tasks-state="tasksState"
      :can-create="canCreate"
      :stale="!!location.last_assigned_task"
      :busy="busy"
      @create-task="$emit('create-task', $event)"
      @edit-task="$emit('edit-task', $event)"
      @cancel-task="$emit('cancel-task', $event)"
      @show-active-tasks="$emit('show-active-tasks')"
    />

    <div class="ocard__visit">
      <div class="ocard__label">Последний визит</div>
      <template v-if="visit">
        <div><span class="ocard__muted">Завершён:</span> <b>{{ visit.at }}</b></div>
        <div><span class="ocard__muted">Инженер:</span> <b>{{ visit.engineer }}</b></div>
        <div v-if="visit.comment"><span class="ocard__muted">Комментарий инженера:</span> {{ visit.comment }}</div>
        <div v-if="visit.undone.length" class="text-danger">Не выполнено: {{ visit.undone.join(', ') }}</div>
      </template>
      <div v-else class="ocard__muted">Последний визит: отсутствует</div>
    </div>
  </div>
</template>

<script>
import CardTaskBlock from '@/components/management/dispatcher/map/CardTaskBlock.vue';
import {
  STATE_BY_KEY, objectFlags, problemLists, tonerLevels, printerInterfaceUrl,
} from '@/components/management/dispatcher/objectState';
import { unixToRu, pluralRu } from '@/components/management/dispatcher/format';

const CMYK_COLORS = { C: '#00a4e4', M: '#ec008c', Y: '#f0c000', K: '#1b1b1b' };
const PACKS = ['пачка', 'пачки', 'пачек'];

/** Completion outcome of a completed task by its field works (spec §9): the card lists what was left undone. */
export function completionMeta(fieldWorks) {
  const works = Array.isArray(fieldWorks) ? fieldWorks : [];
  const undone = works.filter((w) => !w || !w.is_done).map((w) => (w && w.type && w.type.title) || '—');
  const done = works.length - undone.length;
  if (works.length === 0 || undone.length === 0) return { label: 'Выполнена полностью', color: '#17876f', undone: [] };
  if (done > 0) return { label: 'Выполнена частично', color: '#d08b1f', undone };
  return { label: 'Не выполнена', color: '#c0492b', undone };
}

export default {
  name: 'ObjectCard',
  components: { CardTaskBlock },
  props: {
    location: { type: Object, required: true },
    activeTask: { type: Object, default: null },
    extraActiveCount: { type: Number, default: 0 },
    tasksUnavailable: { type: Boolean, default: false },
    // 'ready' | 'loading' | 'error' — `tasksUnavailable` still gates the actions, this only
    // decides what an empty card says: a reload is «Обновление…», not a failed load.
    tasksState: { type: String, default: 'ready' },
    busy: { type: Boolean, default: false },
  },
  data() {
    return { cmykColors: CMYK_COLORS };
  },
  computed: {
    flags() { return objectFlags(this.location); },
    /** The connectivity chip: online / offline / unknown (always the first flag). */
    conn() { return STATE_BY_KEY[this.flags[0]]; },
    sleeping() { return this.flags.includes('sleeping'); },
    problems() { return problemLists(this.location); },
    toner() { return tonerLevels(this.location); },
    interfaceUrl() { return printerInterfaceUrl(this.location); },
    /** Packs of paper on site (not sheets in a tray): «10 пачек», or «—» when unknown. */
    paper() {
      const v = this.location.paper_balance;
      if (v === null || v === undefined || v === '') return { num: '—', unit: '' };
      const n = Number(v);
      return Number.isFinite(n) ? { num: String(v), unit: pluralRu(n, PACKS) } : { num: String(v), unit: '' };
    },
    canCreate() {
      return !this.activeTask && !this.location.last_assigned_task && !this.tasksUnavailable;
    },
    visit() {
      const t = this.location.last_completed_task;
      if (!t) return null;
      const meta = completionMeta(t.field_works);
      return {
        at: unixToRu(t.completed_at),
        engineer: (t.assignee && t.assignee.username) || '—',
        undone: meta.undone,
        comment: t.completion_comment || '',
      };
    },
  },
};
</script>

<style scoped lang="scss">
.ocard {
  width: 360px; max-width: 100%; background: #fff; border-radius: 12px; box-shadow: 0 10px 30px rgba(20, 24, 20, .18);
  padding: 14px 16px; font-size: 13px; color: #171a19; display: flex; flex-direction: column; gap: 12px;
}
.ocard__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; border-bottom: 1px solid #eceee9; padding-bottom: 10px; }
.ocard__name { font-weight: 800; font-size: 15px; }
.ocard__addr { color: #8b918c; font-size: 12px; }
.ocard__label { font-size: 10.5px; letter-spacing: .08em; text-transform: uppercase; color: #9aa09b; font-weight: 800; margin-bottom: 6px; }
.ocard__cmyk-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.ocard__bar { height: 6px; background: #eceee9; border-radius: 4px; overflow: hidden; }
.ocard__bar-fill { height: 100%; }
.ocard__cmyk-txt { display: flex; justify-content: space-between; font-family: monospace; font-size: 11px; margin-top: 3px; }
.ocard__chips { display: flex; gap: 6px; flex-wrap: wrap; }
.ocard__chip {
  display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 700; line-height: 1.4;
  background: #eceee9; color: #4c524d;
  &--online { background: #e3f3ee; color: #17876f; }
  &--offline { background: #e9eaec; color: #6b7078; }
  &--unknown { background: #f1f2f0; color: #8b918c; }
  &--sleeping { background: #efe8fb; color: #6d3fc0; }
}
.ocard__paper {
  display: flex; justify-content: space-between; align-items: baseline; gap: 8px;
  border-top: 1px solid #eceee9; border-bottom: 1px solid #eceee9; padding: 8px 0;
}
.ocard__paper-label { color: #4c524d; i { color: #9aa09b; margin-right: 4px; } }
.ocard__paper-value { white-space: nowrap; b { font-size: 16px; } }
.ocard__problems { background: #f7f8f5; border-radius: 10px; padding: 10px 12px; }
.ocard__plist { font-size: 12px; line-height: 1.35; }
.ocard__plist + .ocard__plist { margin-top: 8px; }
.ocard__plist ul { padding-left: 18px; margin: 2px 0 0; color: #4c524d; }
.ocard__plist li + li { margin-top: 2px; }
.ocard__ptitle { font-weight: 700; }
.ocard__plist--error .ocard__ptitle { color: #c0492b; }
.ocard__plist--service .ocard__ptitle { color: #b8781a; }
.ocard__ok { color: #17876f; font-weight: 700; }
.ocard__muted { color: #8b918c; }
.ocard__iface { color: #17876f; font-weight: 700; }
.ocard__visit { border-top: 1px solid #eceee9; padding-top: 10px; display: flex; flex-direction: column; gap: 3px; }
</style>
